import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

import { apiDeleteTmpStorage } from '#/api/tmpStorage/apiDeleteTmpStorage';
import type { ApiTmpStorageId } from '#/api/types/ApiTypedId';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useQueryParamState } from '#/hooks/useQueryParamState';
import { authApiClientFetchTmpStorage } from '#/modules/auth/api/tmpStorage/authApiFetchTmpStorage';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import { useAuthProcessTmpStorage } from '#/modules/auth/hooks/useAuthProcessTmpStorage';

type AuthTmpStorageContextState = {
  tmpStorage: AuthApiTmpStorage | null;
  clearTmpStorage: () => void;
};

export const AuthTmpStorageContext = createContext<AuthTmpStorageContextState>({
  tmpStorage: null,
  clearTmpStorage: () => {
    throw new Error('not implemented');
  },
});

export function AuthTmpStorageContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useUser();

  const [tmpIdParam, setTmpIdParam] = useQueryParamState({ name: 'tmpId' });
  const [tmpStorage, setTmpStorage] = useState<AuthApiTmpStorage | null>(null);

  const tmpIdToProcess =
    tmpIdParam &&
    !tmpStorage &&
    user?.emailVerified &&
    user.firstName &&
    user.lastName
      ? tmpIdParam
      : null;

  const { authProcessTmpStorage } = useAuthProcessTmpStorage();

  const fetchAndProcessTmpStorage = useCallback(
    async (tmpStorageId: ApiTmpStorageId) => {
      const fetchedTmpStorage =
        await authApiClientFetchTmpStorage(tmpStorageId);

      if (fetchedTmpStorage) {
        setTmpIdParam(undefined, { replace: true });
        await apiDeleteTmpStorage(tmpStorageId);

        const processed = await authProcessTmpStorage({
          tmpStorage: fetchedTmpStorage,
          replaceOnNavigate: true,
        });

        if (!processed) setTmpStorage(fetchedTmpStorage);
      }
    },
    [authProcessTmpStorage, setTmpIdParam],
  );

  useEffect(() => {
    if (tmpIdToProcess) void fetchAndProcessTmpStorage(tmpIdToProcess);
  }, [tmpIdToProcess, fetchAndProcessTmpStorage]);

  const clearTmpStorage = useCallback(() => {
    setTmpStorage(null);
  }, []);

  const contextValue = useMemo(
    () =>
      ({ tmpStorage, clearTmpStorage }) satisfies AuthTmpStorageContextState,
    [tmpStorage, clearTmpStorage],
  );

  return (
    <AuthTmpStorageContext.Provider value={contextValue}>
      {children}
    </AuthTmpStorageContext.Provider>
  );
}

export function useAuthTmpStorageContext() {
  return useContext(AuthTmpStorageContext);
}
